/* ============================================================
   CONTENT LOADER — reads published content + media from Firestore
   and merges it over the in-code seed, so a missing field never
   breaks the page (typed fallbacks). Subscriptions are live, so
   editing a Firestore field updates the page in place.

   Schema (EN-only in v1; shape stays locale-aware):
     content/{locale}   one document holding the whole content tree
                        (flat, like the prototype — edited by dotted
                        path in Phase 3)
     media/{id}         { name, css? , url?, storagePath? }
   ============================================================ */
import { doc, collection, onSnapshot, setDoc } from 'firebase/firestore'
import { db } from '../lib/firestore.js'
import { CONTENT, MEDIA, DEFAULT_LOCALE, DEFAULT_SECTIONS, resolveSections } from './site.js'

export { DEFAULT_LOCALE }

const isObject = (v) => v && typeof v === 'object' && !Array.isArray(v)

// Deep-merge `remote` over `base`. Arrays and primitives from `remote`
// replace `base`; nested objects merge key by key. null/undefined remote
// values fall back to base.
export function mergeDeep(base, remote) {
  if (remote == null) return base
  if (!isObject(base) || !isObject(remote)) return remote
  const out = { ...base }
  for (const key of Object.keys(remote)) {
    out[key] = key in base ? mergeDeep(base[key], remote[key]) : remote[key]
  }
  return out
}

// Live-subscribe to the content document for a locale. Calls onData with
// the merged content (seed <- remote). Returns an unsubscribe function.
export function subscribeContent(locale, onData, onError) {
  const ref = doc(db, 'content', locale)
  return onSnapshot(
    ref,
    (snap) => onData(mergeDeep(CONTENT, snap.exists() ? snap.data() : null)),
    onError,
  )
}

// Live-subscribe to the media collection. Falls back to the in-code MEDIA
// when the collection is empty. Returns an unsubscribe function.
export function subscribeMedia(onData, onError) {
  const ref = collection(db, 'media')
  return onSnapshot(
    ref,
    (snap) => {
      const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }))
      onData(docs.length ? docs : MEDIA)
    },
    onError,
  )
}

// Live-subscribe to the section registry for a locale. Calls onData with the
// resolved, ordered list (derived nums; hero pinned). Falls back to the seed.
export function subscribeSections(locale, onData, onError) {
  const ref = doc(db, 'siteSections', locale)
  return onSnapshot(
    ref,
    (snap) => onData(resolveSections(snap.exists() ? snap.data().items : DEFAULT_SECTIONS)),
    onError,
  )
}

// Persist the section registry (admin only, enforced by rules). Stores only
// the editable shape { key, label, hidden } in order.
export function saveSiteSections(locale, items) {
  const clean = items.map((it) => ({ key: it.key, label: it.label, hidden: !!it.hidden }))
  return setDoc(doc(db, 'siteSections', locale), { items: clean })
}

// Site-wide settings (enquiry recipient + social links). Public read so the
// footer can show social icons; admin write. Single doc settings/global.
export const DEFAULT_SETTINGS = { enquiryEmail: '', social: { instagram: '', x: '', facebook: '' } }

export function subscribeSettings(onData, onError) {
  return onSnapshot(
    doc(db, 'settings', 'global'),
    (snap) => {
      const d = snap.exists() ? snap.data() : {}
      onData({
        ...DEFAULT_SETTINGS,
        ...d,
        social: { ...DEFAULT_SETTINGS.social, ...(d.social || {}) },
      })
    },
    onError,
  )
}

export function saveSettings(partial) {
  return setDoc(doc(db, 'settings', 'global'), partial, { merge: true })
}
